<script setup>
import { ref, computed, watch, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';
import { onClickOutside } from '@vueuse/core';
import SmartTitleInput from 'dashboard/modules/tasks/components/SmartTitleInput.vue';
import AutomationSelect from 'dashboard/modules/kanban/components/automations/AutomationSelect.vue';
import TaskGroupItemRecurrencePopover from './TaskGroupItemRecurrencePopover.vue';

const props = defineProps({
  item: { type: Object, required: true },
});
const emit = defineEmits(['update', 'remove', 'title-enter', 'open-detail']);

const { t } = useI18n();
const agents = useMapGetter('agents/getAgents');
const accountLabels = useMapGetter('labels/getLabels');

// ── título inline ────────────────────────────────────────────────
const titleRef = ref(null);
const editingTitle = ref(false);
const titleDraft = ref('');
const smartPendingFields = ref({});

function startEditTitle(e) {
  e.stopPropagation();
  titleDraft.value = props.item.title ?? '';
  smartPendingFields.value = {};
  editingTitle.value = true;
  const clickX = e.clientX;
  const clickY = e.clientY;
  nextTick(() => {
    const el = titleRef.value?.$el ?? titleRef.value;
    if (!el) return;
    const editorEl = el.querySelector('[contenteditable]') ?? el;
    editorEl.focus();
    const range =
      document.caretRangeFromPoint?.(clickX, clickY) ??
      (() => {
        const pos = document.caretPositionFromPoint?.(clickX, clickY);
        if (!pos) return null;
        const r = document.createRange();
        r.setStart(pos.offsetNode, pos.offset);
        r.collapse(true);
        return r;
      })();
    if (range && editorEl.contains(range.startContainer)) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  });
}

function onSmartPendingFields(fields) {
  smartPendingFields.value = fields;
}

function focusTitle() {
  titleDraft.value = props.item.title ?? '';
  smartPendingFields.value = {};
  editingTitle.value = true;
  nextTick(() => {
    const el = titleRef.value?.$el ?? titleRef.value;
    if (!el) return;
    const editorEl = el.querySelector('[contenteditable]') ?? el;
    editorEl.focus();
  });
}

function commitTitle() {
  editingTitle.value = false;
  const fields = smartPendingFields.value;
  const updated = { ...props.item };
  if (titleDraft.value.trim()) updated.title = titleDraft.value.trim();
  if (fields.priority) updated.priority = fields.priority;
  if (fields.assignee_id) updated.assignee_id = fields.assignee_id;
  if (fields.task_type) updated.task_type = fields.task_type;
  if (fields.label_list?.length) {
    const existing = props.item.label_list ?? [];
    updated.label_list = [...new Set([...existing, ...fields.label_list])];
  }
  emit('update', updated);
  smartPendingFields.value = {};
}

// ── menus ────────────────────────────────────────────────────────
const showAssigneeMenu = ref(false);
const showTypeMenu = ref(false);
const showPriorityMenu = ref(false);
const showLabelMenu = ref(false);

function closeMenus() {
  showAssigneeMenu.value = false;
  showTypeMenu.value = false;
  showPriorityMenu.value = false;
  showLabelMenu.value = false;
}

// ── prazo em dias (campo legado) → agora usa due_value/due_unit ─
const DUE_UNITS = [
  { key: 'minutes', label: 'Minutos' },
  { key: 'hours', label: 'Horas' },
  { key: 'days', label: 'Dias' },
  { key: 'months', label: 'Meses' },
  { key: 'years', label: 'Anos' },
];

const dueUnitOptions = computed(() =>
  DUE_UNITS.map(u => ({ value: u.key, label: u.label }))
);

function patch(field, value) {
  emit('update', { ...props.item, [field]: value });
}

// ── tipos ────────────────────────────────────────────────────────
const TASK_TYPES = [
  { key: 'general', label: 'Geral', icon: 'i-lucide-circle-dot' },
  { key: 'call', label: 'Ligação', icon: 'i-lucide-phone' },
  { key: 'email', label: 'E-mail', icon: 'i-lucide-mail' },
  { key: 'meeting', label: 'Reunião', icon: 'i-lucide-calendar' },
  { key: 'follow_up', label: 'Follow-up', icon: 'i-lucide-refresh-cw' },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'i-lucide-message-circle' },
  { key: 'contract', label: 'Contrato', icon: 'i-lucide-file-signature' },
  { key: 'payment', label: 'Pagamento', icon: 'i-lucide-banknote' },
];

const typeSearch = ref('');
const typeHighlight = ref(0);

const filteredTaskTypes = computed(() => {
  const q = typeSearch.value.toLowerCase();
  if (!q) return TASK_TYPES;
  return TASK_TYPES.filter(o => o.label.toLowerCase().includes(q)).sort(
    (a, b) =>
      a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
  );
});

const typeNavigable = computed(() =>
  filteredTaskTypes.value.map(o => ({ id: o.key }))
);

watch(typeNavigable, () => {
  typeHighlight.value = 0;
});
watch(typeSearch, () => {
  typeHighlight.value = 0;
});

const currentType = computed(
  () =>
    TASK_TYPES.find(tp => tp.key === (props.item.task_type ?? 'general')) ??
    TASK_TYPES[0]
);

function toggleTypeMenu(e) {
  e.stopPropagation();
  const next = !showTypeMenu.value;
  closeMenus();
  showTypeMenu.value = next;
  if (next) {
    typeSearch.value = '';
    const idx = TASK_TYPES.findIndex(tp => tp.key === props.item.task_type);
    typeHighlight.value = idx >= 0 ? idx : 0;
  }
}

function selectType(type) {
  showTypeMenu.value = false;
  typeSearch.value = '';
  patch('task_type', type.key);
}

function onTypeKeydown(e) {
  if (!showTypeMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    typeHighlight.value = Math.min(
      typeHighlight.value + 1,
      typeNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    typeHighlight.value = Math.max(typeHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = typeNavigable.value[typeHighlight.value];
    if (item) selectType(TASK_TYPES.find(tp => tp.key === item.id));
  } else if (e.key === 'Escape') {
    showTypeMenu.value = false;
    typeSearch.value = '';
  } else if (e.key === 'Backspace') {
    typeSearch.value = typeSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    typeSearch.value += e.key;
    typeHighlight.value = 0;
  }
}

watch(showTypeMenu, val => {
  if (val) window.addEventListener('keydown', onTypeKeydown);
  else {
    window.removeEventListener('keydown', onTypeKeydown);
    typeSearch.value = '';
  }
});

// ── prioridade ───────────────────────────────────────────────────
const PRIORITY_OPTIONS = [
  {
    key: 'urgent',
    label: 'Urgente',
    bg: 'bg-n-ruby-2',
    text: 'text-n-ruby-11',
    dot: 'text-n-ruby-9',
  },
  {
    key: 'high',
    label: 'Prioridade',
    bg: 'bg-n-amber-2',
    text: 'text-n-amber-11',
    dot: 'text-n-amber-9',
  },
  {
    key: 'normal',
    label: 'Normal',
    bg: 'bg-n-blue-2',
    text: 'text-n-blue-11',
    dot: 'text-n-blue-9',
  },
  {
    key: 'no_priority',
    label: 'Baixa',
    bg: '',
    text: '',
    dot: '',
    bgColor: 'rgba(119,123,132,0.12)',
    textColor: '#777B84',
  },
];

const PRIORITY_BAR = {
  urgent: { class: 'bg-n-ruby-9' },
  high: { class: 'bg-n-amber-9' },
  normal: { class: 'bg-n-blue-9' },
  no_priority: { style: { backgroundColor: '#777B84' } },
};

const prioritySearch = ref('');
const priorityHighlight = ref(0);

const filteredPriorityOptions = computed(() => {
  const q = prioritySearch.value.toLowerCase();
  if (!q) return PRIORITY_OPTIONS;
  return PRIORITY_OPTIONS.filter(o => o.label.toLowerCase().includes(q)).sort(
    (a, b) =>
      a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
  );
});

const priorityNavigable = computed(() =>
  filteredPriorityOptions.value.map(o => ({ id: o.key }))
);

watch(priorityNavigable, () => {
  priorityHighlight.value = 0;
});
watch(prioritySearch, () => {
  priorityHighlight.value = 0;
});

const currentPriority = computed(
  () =>
    PRIORITY_OPTIONS.find(
      p => p.key === (props.item.priority ?? 'no_priority')
    ) ?? null
);

const priorityBarClass = computed(
  () => PRIORITY_BAR[props.item.priority]?.class ?? ''
);
const priorityBarStyle = computed(
  () => PRIORITY_BAR[props.item.priority]?.style ?? {}
);

function togglePriorityMenu(e) {
  e.stopPropagation();
  const next = !showPriorityMenu.value;
  closeMenus();
  showPriorityMenu.value = next;
  if (next) {
    prioritySearch.value = '';
    const idx = PRIORITY_OPTIONS.findIndex(p => p.key === props.item.priority);
    priorityHighlight.value = idx >= 0 ? idx : 0;
  }
}

function selectPriority(key) {
  showPriorityMenu.value = false;
  prioritySearch.value = '';
  patch('priority', key);
}

function onPriorityKeydown(e) {
  if (!showPriorityMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    priorityHighlight.value = Math.min(
      priorityHighlight.value + 1,
      priorityNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    priorityHighlight.value = Math.max(priorityHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = priorityNavigable.value[priorityHighlight.value];
    if (item) selectPriority(item.id);
  } else if (e.key === 'Escape') {
    showPriorityMenu.value = false;
    prioritySearch.value = '';
  } else if (e.key === 'Backspace') {
    prioritySearch.value = prioritySearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    prioritySearch.value += e.key;
    priorityHighlight.value = 0;
  }
}

watch(showPriorityMenu, val => {
  if (val) window.addEventListener('keydown', onPriorityKeydown);
  else {
    window.removeEventListener('keydown', onPriorityKeydown);
    prioritySearch.value = '';
  }
});

// ── responsável ──────────────────────────────────────────────────
const assigneeSearch = ref('');
const assigneeHighlight = ref(0);

const filteredAssigneeAgents = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  if (!q) return agents.value ?? [];
  return (agents.value ?? [])
    .filter(a => a.name.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
    );
});

const assigneeNavigable = computed(() => {
  if (!assigneeSearch.value)
    return [
      { id: 'card_leader' },
      ...(agents.value ?? []).map(a => ({ id: a.id })),
      { id: null },
    ];
  const q = assigneeSearch.value.toLowerCase();
  const items = filteredAssigneeAgents.value.map(a => ({ id: a.id }));
  const showLeader = 'líder do card'.includes(q);
  const showUnassign = 'remover responsável'.includes(q);
  return [
    ...(showLeader ? [{ id: 'card_leader' }] : []),
    ...items,
    ...(showUnassign ? [{ id: null }] : []),
  ];
});

watch(assigneeNavigable, () => {
  assigneeHighlight.value = 0;
});
watch(assigneeSearch, () => {
  assigneeHighlight.value = 0;
});

const selectedAssignee = computed(
  () => (agents.value ?? []).find(a => a.id === props.item.assignee_id) ?? null
);

function toggleAssigneeMenu(e) {
  e.stopPropagation();
  const next = !showAssigneeMenu.value;
  closeMenus();
  showAssigneeMenu.value = next;
  if (next) {
    assigneeSearch.value = '';
    const idx = assigneeNavigable.value.findIndex(
      i => i.id === (props.item.assignee_id ?? null)
    );
    assigneeHighlight.value = idx >= 0 ? idx : 0;
  }
}

function selectAssignee(agentOrNull) {
  showAssigneeMenu.value = false;
  assigneeSearch.value = '';
  const id = agentOrNull?.id ?? null;
  patch('assignee_id', id);
}

function onAssigneeKeydown(e) {
  if (!showAssigneeMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    assigneeHighlight.value = Math.min(
      assigneeHighlight.value + 1,
      assigneeNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    assigneeHighlight.value = Math.max(assigneeHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const nav = assigneeNavigable.value[assigneeHighlight.value];
    if (nav) {
      if (nav.id === 'card_leader') {
        selectAssignee({ id: 'card_leader' });
      } else if (nav.id === null) {
        selectAssignee(null);
      } else {
        const agent = (agents.value ?? []).find(a => a.id === nav.id);
        selectAssignee(agent ?? null);
      }
    }
  } else if (e.key === 'Escape') {
    showAssigneeMenu.value = false;
    assigneeSearch.value = '';
  } else if (e.key === 'Backspace') {
    assigneeSearch.value = assigneeSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    assigneeSearch.value += e.key;
  }
}

watch(showAssigneeMenu, val => {
  if (val) window.addEventListener('keydown', onAssigneeKeydown);
  else {
    window.removeEventListener('keydown', onAssigneeKeydown);
    assigneeSearch.value = '';
  }
});

// ── labels ───────────────────────────────────────────────────────
const labelSearch = ref('');
const labelHighlight = ref(0);

const filteredLabels = computed(() => {
  const q = labelSearch.value.toLowerCase();
  if (!q) return accountLabels.value ?? [];
  return (accountLabels.value ?? [])
    .filter(l => l.title.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.title.toLowerCase().indexOf(q) - b.title.toLowerCase().indexOf(q)
    );
});

watch(filteredLabels, () => {
  labelHighlight.value = 0;
});
watch(labelSearch, () => {
  labelHighlight.value = 0;
});

const selectedLabels = computed(() => props.item.label_list ?? []);

function isLabelActive(title) {
  return selectedLabels.value.includes(title);
}

function toggleLabel(title, e) {
  e.stopPropagation();
  const list = [...selectedLabels.value];
  const idx = list.indexOf(title);
  if (idx === -1) list.push(title);
  else list.splice(idx, 1);
  patch('label_list', list);
}

function removeTag(tag, e) {
  e.stopPropagation();
  patch(
    'label_list',
    selectedLabels.value.filter(l => l !== tag)
  );
}

function openLabelMenu(e) {
  e.stopPropagation();
  const next = !showLabelMenu.value;
  closeMenus();
  showLabelMenu.value = next;
  labelSearch.value = '';
  labelHighlight.value = 0;
}

function onLabelKeydown(e) {
  if (!showLabelMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    labelHighlight.value = Math.min(
      labelHighlight.value + 1,
      filteredLabels.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    labelHighlight.value = Math.max(labelHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const lbl = filteredLabels.value[labelHighlight.value];
    if (lbl) toggleLabel(lbl.title, e);
  } else if (e.key === 'Escape') {
    showLabelMenu.value = false;
    labelSearch.value = '';
  } else if (e.key === 'Backspace') {
    labelSearch.value = labelSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    labelSearch.value += e.key;
  }
}

watch(showLabelMenu, val => {
  if (val) window.addEventListener('keydown', onLabelKeydown);
  else {
    window.removeEventListener('keydown', onLabelKeydown);
    labelSearch.value = '';
  }
});

// ── tag color ────────────────────────────────────────────────────
function tagStyle(label) {
  const accountLabel = (accountLabels.value ?? []).find(
    l => l.title?.toLowerCase() === label.toLowerCase()
  );
  if (accountLabel?.color) {
    return { bg: `${accountLabel.color}22`, text: accountLabel.color };
  }
  return { bg: 'rgba(107,114,128,0.1)', text: '#4b5563' };
}

// ── avatar ───────────────────────────────────────────────────────
const { avatarColor, initials: agentInitials } = useAvatarColor();
function initials(name) {
  return agentInitials(name ?? '') || '?';
}

// ── checklist badge ───────────────────────────────────────────────
const checklistProgress = computed(() => {
  const items = props.item.checklist_items ?? [];
  if (!items.length) return null;
  const done = items.filter(i => i.completed).length;
  return { done, total: items.length, label: `${done}/${items.length}` };
});

// ── prazo relativo ────────────────────────────────────────────────
const dueValue = computed(() => props.item.due_value ?? 1);
const dueUnit = computed(() => props.item.due_unit ?? 'days');

// ── recorrência ──────────────────────────────────────────────────
const showRecurrencePopover = ref(false);

function toggleRecurrencePopover(e) {
  e.stopPropagation();
  closeMenus();
  showRecurrencePopover.value = !showRecurrencePopover.value;
}

function onRecurrenceUpdate(fields) {
  emit('update', { ...props.item, ...fields });
}

const RECURRENCE_LABELS = {
  daily: 'TASKS.RECURRENCE.DAILY',
  weekdays: 'TASKS.RECURRENCE.WEEKDAYS',
  weekly: 'TASKS.RECURRENCE.WEEKLY',
  monthly: 'TASKS.RECURRENCE.MONTHLY',
  custom: 'TASKS.RECURRENCE.CUSTOM',
};

const recurrenceLabel = computed(() => {
  const type = props.item.recurrence_type;
  if (!type) return null;
  if (type === 'custom' && props.item.recurrence_interval)
    return `${props.item.recurrence_interval}x`;
  return RECURRENCE_LABELS[type] ? t(RECURRENCE_LABELS[type]) : type;
});

// ── confirmação de remoção ────────────────────────────────────────
const showRemoveConfirm = ref(false);
const removeConfirmRef = ref(null);

function requestRemove(e) {
  e.stopPropagation();
  closeMenus();
  showRemoveConfirm.value = true;
}

function confirmRemove() {
  showRemoveConfirm.value = false;
  emit('remove');
}

function cancelRemove() {
  showRemoveConfirm.value = false;
}

onClickOutside(removeConfirmRef, () => {
  showRemoveConfirm.value = false;
});

// ── click outside ────────────────────────────────────────────────
const itemRef = ref(null);
onClickOutside(itemRef, closeMenus);

onUnmounted(() => {
  window.removeEventListener('keydown', onPriorityKeydown);
  window.removeEventListener('keydown', onAssigneeKeydown);
  window.removeEventListener('keydown', onTypeKeydown);
  window.removeEventListener('keydown', onLabelKeydown);
});

defineExpose({ focusTitle });
</script>

<template>
  <div
    ref="itemRef"
    class="task-drag-item flex items-stretch border-b border-n-weak last:border-b-0 group relative min-h-[52px] transition-colors hover:bg-n-alpha-1 cursor-grab active:cursor-grabbing"
    @click="closeMenus"
  >
    <!-- Borda lateral de prioridade -->
    <span
      class="w-[3px] flex-shrink-0 self-stretch"
      :class="priorityBarClass"
      :style="priorityBarStyle"
    />

    <!-- Grip handle (visual only) -->
    <span
      class="flex-shrink-0 self-center px-1 opacity-0 group-hover:opacity-100 transition-opacity text-n-slate-9"
    >
      <span class="i-lucide-grip-vertical size-4" />
    </span>

    <!-- ESQUERDA: título + sublinha -->
    <div class="flex-1 min-w-0 py-2 pr-2 flex flex-col justify-center">
      <!-- Título -->
      <SmartTitleInput
        v-if="editingTitle"
        ref="titleRef"
        v-model="titleDraft"
        autofocus
        class="text-sm font-medium text-n-slate-12"
        @update:pending-fields="onSmartPendingFields"
        @blur="commitTitle"
        @submit="
          commitTitle();
          emit('title-enter');
        "
        @cancel="editingTitle = false"
        @click.stop
      />
      <span
        v-else
        class="text-sm font-medium truncate select-none leading-tight text-n-slate-12"
        @dblclick="startEditTitle"
      >
        {{ item.title || t('TASKS.FORM.TITLE_PLACEHOLDER') }}
      </span>

      <!-- Sublinha: prazo relativo + labels -->
      <div class="flex items-center flex-wrap gap-1 overflow-visible">
        <!-- Labels -->
        <template v-if="selectedLabels.length">
          <span
            v-for="label in selectedLabels"
            :key="label"
            class="group/tag inline-flex items-center h-5 text-[11px] leading-none font-medium px-1.5 rounded shrink-0"
            :style="{
              backgroundColor: tagStyle(label).bg,
              color: tagStyle(label).text,
            }"
          >
            {{ label }}
            <span
              class="overflow-hidden transition-all duration-150 max-w-0 group-hover/tag:max-w-[12px] flex items-center justify-center"
            >
              <button
                class="flex items-center justify-center hover:bg-black/10 rounded-full ml-[2px]"
                @click="removeTag(label, $event)"
              >
                <span class="i-lucide-x size-[8px] flex-shrink-0" />
              </button>
            </span>
          </span>
        </template>

        <!-- Botão adicionar label -->
        <div class="relative shrink-0" @click.stop @dblclick.stop>
          <button
            class="opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity focus:outline-none"
            :class="
              showLabelMenu && labelSearch ? 'opacity-100 !opacity-100' : ''
            "
            @click="openLabelMenu"
          >
            <span
              v-if="showLabelMenu && labelSearch"
              class="text-[9px] font-medium text-n-slate-10"
              >{{ labelSearch }}
            </span>
            <span v-else class="i-lucide-tag size-3 text-n-slate-10" />
          </button>
          <div
            v-if="showLabelMenu"
            class="absolute left-0 top-5 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-44 overflow-hidden py-1"
            @click.stop
          >
            <button
              v-for="(lbl, idx) in filteredLabels"
              :key="lbl.id"
              class="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-n-alpha-1 transition-colors focus:outline-none"
              :class="
                idx === labelHighlight
                  ? 'bg-n-alpha-1 text-n-slate-12 font-medium'
                  : isLabelActive(lbl.title)
                    ? 'text-n-slate-12 font-medium'
                    : 'text-n-slate-11'
              "
              @click="toggleLabel(lbl.title, $event)"
              @mouseenter="labelHighlight = idx"
            >
              <span
                class="size-2.5 rounded-full flex-shrink-0"
                :style="{ backgroundColor: lbl.color }"
              />
              <span class="truncate">{{ lbl.title }}</span>
              <span
                v-if="isLabelActive(lbl.title)"
                class="i-lucide-check size-3 ml-auto flex-shrink-0 text-n-brand"
              />
            </button>
            <p
              v-if="filteredLabels.length === 0"
              class="px-3 py-2 text-xs text-n-slate-10 italic text-center"
            >
              {{ t('TASKS.TAGS.EMPTY') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- DIREITA: prazo label · prioridade · responsável · tipo · remover -->
    <div class="flex items-center gap-2 pr-3 flex-shrink-0">
      <!-- Prazo relativo -->
      <div class="flex items-center gap-1 shrink-0" @click.stop>
        <input
          :value="dueValue"
          type="number"
          min="0"
          class="reset-base !w-14 shrink-0 grow-0 rounded-lg border border-n-weak bg-n-surface-2 px-1.5 py-1 text-xs text-n-slate-12 focus:outline-none appearance-auto"
          @input="patch('due_value', Number($event.target.value))"
          @click.stop
        />
        <AutomationSelect
          compact
          small
          :model-value="dueUnit"
          :options="dueUnitOptions"
          @update:model-value="patch('due_unit', $event)"
        />
        <!-- Recorrência -->
        <div class="relative flex-shrink-0">
          <button
            class="flex items-center gap-1 px-2 py-1 rounded-lg border transition-colors focus:outline-none"
            :class="
              item.recurrence_type
                ? 'border-n-weak bg-n-surface-2 text-n-brand'
                : 'border-transparent text-n-slate-9 hover:border-n-weak hover:bg-n-surface-2 hover:text-n-slate-11'
            "
            :title="$t('TASKS.RECURRENCE.LABEL')"
            @click="toggleRecurrencePopover"
          >
            <span class="i-lucide-repeat size-3 flex-shrink-0" />
            <span v-if="recurrenceLabel" class="text-[11px] font-medium">{{
              recurrenceLabel
            }}</span>
          </button>
          <TaskGroupItemRecurrencePopover
            v-if="showRecurrencePopover"
            :item="item"
            @update="onRecurrenceUpdate"
            @close="showRecurrencePopover = false"
          />
        </div>
      </div>

      <!-- Checklist badge -->
      <div
        v-if="checklistProgress"
        class="flex items-center gap-1 text-xs flex-shrink-0"
        :class="
          checklistProgress.done === checklistProgress.total
            ? 'text-n-teal-11'
            : 'text-n-slate-10'
        "
      >
        <span class="i-lucide-check-square size-3.5 flex-shrink-0" />
        <span>{{ checklistProgress.label }}</span>
      </div>

      <!-- Prioridade badge -->
      <div class="relative flex-shrink-0">
        <button
          class="flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded transition-colors focus:outline-none"
          :class="
            showPriorityMenu && prioritySearch
              ? 'bg-n-alpha-2 border border-n-brand text-n-slate-10'
              : currentPriority && !currentPriority.bgColor
                ? [currentPriority.bg, currentPriority.text]
                : currentPriority
                  ? ''
                  : 'opacity-0 group-hover:opacity-60 bg-n-surface-2 text-n-slate-10'
          "
          :style="
            currentPriority?.bgColor && !(showPriorityMenu && prioritySearch)
              ? {
                  backgroundColor: currentPriority.bgColor,
                  color: currentPriority.textColor,
                }
              : {}
          "
          @click="togglePriorityMenu"
        >
          <template v-if="showPriorityMenu && prioritySearch">
            {{ prioritySearch }}
          </template>
          <template v-else>
            <span
              class="i-lucide-flag size-2.5 flex-shrink-0"
              :style="
                currentPriority?.textColor
                  ? { color: currentPriority.textColor }
                  : {}
              "
              :class="currentPriority?.dot"
            />
            {{
              currentPriority ? currentPriority.label : t('TASKS.PRIORITY.NONE')
            }}
          </template>
        </button>
        <div
          v-if="showPriorityMenu"
          class="absolute right-0 top-7 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-36 overflow-hidden py-1"
          @click.stop
        >
          <button
            v-for="opt in filteredPriorityOptions"
            :key="opt.key"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              priorityNavigable.findIndex(i => i.id === opt.key) ===
              priorityHighlight
                ? opt.text
                  ? 'font-medium bg-n-alpha-1 ' + opt.text
                  : 'font-medium bg-n-alpha-1'
                : item.priority === opt.key
                  ? opt.text
                    ? 'font-medium ' + opt.text
                    : 'font-medium'
                  : 'text-n-slate-12'
            "
            :style="
              opt.textColor &&
              (priorityNavigable.findIndex(i => i.id === opt.key) ===
                priorityHighlight ||
                item.priority === opt.key)
                ? { color: opt.textColor }
                : {}
            "
            @click="selectPriority(opt.key)"
            @mouseenter="
              priorityHighlight = priorityNavigable.findIndex(
                i => i.id === opt.key
              )
            "
          >
            <span
              class="i-lucide-flag size-3 flex-shrink-0"
              :class="opt.dot || ''"
              :style="opt.textColor && !opt.dot ? { color: opt.textColor } : {}"
            />
            {{ opt.label }}
          </button>
          <p
            v-if="prioritySearch && !filteredPriorityOptions.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
          </p>
        </div>
      </div>

      <!-- Responsável -->
      <div class="relative flex-shrink-0">
        <button
          class="size-7 rounded-full flex items-center justify-center hover:opacity-80 transition-all flex-shrink-0 focus:outline-none overflow-hidden"
          :style="
            showAssigneeMenu && assigneeSearch
              ? {}
              : item.assignee_id === 'card_leader'
                ? {}
                : selectedAssignee?.thumbnail
                  ? {
                      backgroundImage: `url(${selectedAssignee.thumbnail})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : selectedAssignee
                    ? { backgroundColor: avatarColor(selectedAssignee) }
                    : {}
          "
          :class="
            showAssigneeMenu && assigneeSearch
              ? 'bg-n-alpha-2 border border-n-brand'
              : item.assignee_id === 'card_leader'
                ? 'bg-n-brand'
                : !selectedAssignee
                  ? 'bg-n-surface-2'
                  : ''
          "
          :title="
            item.assignee_id === 'card_leader'
              ? t('KANBAN.TASK_GROUPS.LEADER.CARD_LEADER_ASSIGNEE')
              : selectedAssignee?.name
          "
          @click="toggleAssigneeMenu"
        >
          <span
            v-if="showAssigneeMenu && assigneeSearch"
            class="text-[9px] font-medium text-n-slate-10 truncate px-0.5"
            >{{ assigneeSearch }}
          </span>
          <span
            v-else-if="item.assignee_id === 'card_leader'"
            class="i-lucide-crown size-3.5 text-white flex-shrink-0"
          />
          <span
            v-else-if="selectedAssignee && !selectedAssignee.thumbnail"
            class="text-[11px] font-bold text-white"
            >{{ initials(selectedAssignee.name) }}
          </span>
          <span
            v-else-if="!selectedAssignee"
            class="i-lucide-user-plus size-4 text-n-slate-9 flex-shrink-0"
          />
        </button>

        <div
          v-if="showAssigneeMenu"
          class="absolute right-0 top-10 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-52 overflow-hidden py-1"
          @click.stop
        >
          <!-- Líder do card -->
          <button
            v-if="assigneeNavigable.some(i => i.id === 'card_leader')"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors focus:outline-none"
            :class="
              assigneeHighlight ===
              assigneeNavigable.findIndex(i => i.id === 'card_leader')
                ? 'bg-n-alpha-1 text-n-brand font-medium'
                : item.assignee_id === 'card_leader'
                  ? 'text-n-brand font-medium hover:bg-n-alpha-1'
                  : 'text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="selectAssignee({ id: 'card_leader' })"
            @mouseenter="
              assigneeHighlight = assigneeNavigable.findIndex(
                i => i.id === 'card_leader'
              )
            "
          >
            <div
              class="size-5 rounded-full flex-shrink-0 bg-n-brand flex items-center justify-center"
            >
              <span class="i-lucide-crown size-3 text-white" />
            </div>
            <span class="truncate">{{
              t('KANBAN.TASK_GROUPS.LEADER.CARD_LEADER_ASSIGNEE')
            }}</span>
            <span
              v-if="item.assignee_id === 'card_leader'"
              class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
            />
          </button>
          <div
            v-if="
              assigneeNavigable.some(i => i.id === 'card_leader') &&
              !assigneeSearch
            "
            class="border-t border-n-weak my-1"
          />

          <button
            v-for="agent in filteredAssigneeAgents"
            :key="agent.id"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors focus:outline-none"
            :class="
              assigneeNavigable.findIndex(i => i.id === agent.id) ===
              assigneeHighlight
                ? 'bg-n-alpha-1 text-n-brand font-medium'
                : item.assignee_id === agent.id
                  ? 'text-n-brand font-medium hover:bg-n-alpha-1'
                  : 'text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="selectAssignee(agent)"
            @mouseenter="
              assigneeHighlight = assigneeNavigable.findIndex(
                i => i.id === agent.id
              )
            "
          >
            <div
              class="size-5 rounded-full flex-shrink-0"
              :style="
                agent.thumbnail
                  ? {
                      backgroundImage: `url(${agent.thumbnail})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : { backgroundColor: avatarColor(agent) }
              "
            >
              <span
                v-if="!agent.thumbnail"
                class="text-[9px] font-bold text-white size-full flex items-center justify-center"
                >{{ initials(agent.name) }}
              </span>
            </div>
            <span class="truncate">{{ agent.name }}</span>
            <span
              v-if="item.assignee_id === agent.id"
              class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
            />
          </button>
          <p
            v-if="assigneeSearch && !assigneeNavigable.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
          </p>
          <template v-if="assigneeNavigable.some(i => i.id === null)">
            <div v-if="!assigneeSearch" class="border-t border-n-weak my-1" />
            <button
              class="w-full flex items-center gap-2 px-3 py-2 text-sm whitespace-nowrap transition-colors focus:outline-none"
              :class="
                assigneeHighlight ===
                assigneeNavigable.findIndex(i => i.id === null)
                  ? 'bg-n-alpha-1 text-n-brand font-medium'
                  : 'text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="selectAssignee(null)"
              @mouseenter="
                assigneeHighlight = assigneeNavigable.findIndex(
                  i => i.id === null
                )
              "
            >
              <span class="i-lucide-user-x size-3.5 flex-shrink-0" />
              {{ t('TASKS.MODAL.UNASSIGN') }}
            </button>
          </template>
        </div>
      </div>

      <!-- Tipo de tarefa -->
      <div class="relative flex-shrink-0">
        <button
          class="flex items-center justify-center size-9 rounded transition-colors focus:outline-none"
          :class="
            showTypeMenu && typeSearch
              ? 'bg-n-alpha-2 border border-n-brand hover:bg-n-alpha-2'
              : 'hover:bg-n-alpha-2'
          "
          :title="currentType.label"
          @click="toggleTypeMenu"
        >
          <span
            v-if="showTypeMenu && typeSearch"
            class="text-[9px] font-medium text-n-slate-10 truncate px-1"
            >{{ typeSearch }}
          </span>
          <span
            v-else
            class="size-7 text-n-slate-9"
            :class="[currentType.icon]"
          />
        </button>
        <div
          v-if="showTypeMenu"
          class="absolute right-0 top-10 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-40 overflow-hidden py-1"
          @click.stop
        >
          <button
            v-for="type in filteredTaskTypes"
            :key="type.key"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              typeNavigable.findIndex(i => i.id === type.key) === typeHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : type.key === item.task_type
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="selectType(type)"
            @mouseenter="
              typeHighlight = typeNavigable.findIndex(i => i.id === type.key)
            "
          >
            <span class="size-4 flex-shrink-0" :class="[type.icon]" />
            {{ type.label }}
            <span
              v-if="type.key === item.task_type"
              class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
            />
          </button>
          <p
            v-if="typeSearch && !filteredTaskTypes.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('TASKS.SMART_INPUT.NOT_FOUND') }}
          </p>
        </div>
      </div>

      <!-- Botão abrir detalhe -->
      <button
        class="size-9 flex items-center justify-center rounded border border-n-container bg-n-background text-n-slate-9 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-n-alpha-2 flex-shrink-0"
        :title="t('KANBAN.TASK_GROUPS.ITEM.OPEN_DETAIL')"
        @click.stop="emit('open-detail')"
      >
        <span class="i-lucide-chevron-right size-4" />
      </button>

      <!-- Botão remover -->
      <div ref="removeConfirmRef" class="relative flex-shrink-0">
        <button
          class="size-9 flex items-center justify-center rounded border border-n-container bg-n-background text-n-slate-9 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-n-alpha-2 flex-shrink-0"
          :class="
            showRemoveConfirm
              ? '!opacity-100 !text-n-ruby-11 !border-n-ruby-6 !bg-n-ruby-2'
              : ''
          "
          @click.stop="requestRemove"
        >
          <span class="i-lucide-x size-4" />
        </button>
        <div
          v-if="showRemoveConfirm"
          class="absolute right-0 top-10 z-40 bg-n-background border border-n-weak rounded-xl shadow-xl py-2 px-3 flex flex-col gap-2 min-w-[148px]"
          @click.stop
        >
          <p class="text-xs text-n-slate-11 whitespace-nowrap">
            {{ t('KANBAN.TASK_GROUPS.ITEM.REMOVE_CONFIRM') }}
          </p>
          <div class="flex gap-1.5">
            <button
              class="flex-1 text-xs font-medium px-2 py-1 rounded-lg bg-n-ruby-9 text-white hover:bg-n-ruby-10 transition-colors"
              @click.stop="confirmRemove"
            >
              {{ t('KANBAN.TASK_GROUPS.ITEM.REMOVE_YES') }}
            </button>
            <button
              class="flex-1 text-xs font-medium px-2 py-1 rounded-lg bg-n-surface-2 text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click.stop="cancelRemove"
            >
              {{ t('KANBAN.TASK_GROUPS.ITEM.REMOVE_NO') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
